export type MentionLink = {
  name: string | null | undefined;
  href: string | null | undefined;
};

type DecodedChar = { char: string; start: number; end: number };

const TAG_PATTERN = /<!--[\s\S]*?-->|<(?:[^>"']|"[^"]*"|'[^']*')*>/g;
const ENTITY_PATTERN = /^&(#\d+|#x[0-9a-f]+|[a-z]+\d*);/i;
const VOID_TAGS = ['br', 'hr', 'img', 'input', 'wbr'];
// Text inside these is never linked: headings stay plain, existing links are left alone.
const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  lsquo: "'",
  rsquo: "'",
  ldquo: '"',
  rdquo: '"',
};

// Curly and straight quotes are treated alike so “The Thunder” matches "The Thunder".
const normalizeChar = (char: string): string => {
  if (/[‘’‚‛]/.test(char)) return "'";
  if (/[“”„‟]/.test(char)) return '"';
  if (char === ' ') return ' ';
  return char;
};

const normalize = (value: string): string =>
  value.split('').map(normalizeChar).join('');

const escapeAttribute = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Built at runtime: the es5 target rejects the u flag in a regex literal.
const WORD_CHAR = new RegExp('[\\p{L}\\p{M}\\p{N}_]', 'u');

const isWordChar = (char: string | undefined): boolean =>
  !!char && WORD_CHAR.test(char);

const decodeEntity = (entity: string): string | null => {
  if (entity[0] !== '#') {
    return NAMED_ENTITIES[entity.toLowerCase()] ?? null;
  }

  const isHex = entity[1].toLowerCase() === 'x';
  const codePoint = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);

  return codePoint > 0 && codePoint <= 0x10ffff
    ? String.fromCodePoint(codePoint)
    : null;
};

/** Decodes a raw text run, keeping the raw slice each decoded char came from. */
const decodeText = (raw: string): DecodedChar[] => {
  const chars: DecodedChar[] = [];
  let index = 0;

  while (index < raw.length) {
    const entity =
      raw[index] === '&' ? raw.slice(index).match(ENTITY_PATTERN) : null;
    const end = index + (entity ? entity[0].length : 1);
    // An unknown entity becomes a placeholder that can never be part of a name.
    const decoded = entity ? decodeEntity(entity[1]) ?? '�' : raw[index];

    decoded
      .split('')
      .forEach((char) =>
        chars.push({ char: normalizeChar(char), start: index, end })
      );
    index = end;
  }

  return chars;
};

/** Raw [start, end) of the first whole-word match of `pattern` in a text run. */
const findMention = (
  raw: string,
  pattern: string
): { start: number; end: number } | null => {
  const chars = decodeText(raw);
  const text = chars.map(({ char }) => char).join('');
  const matcher = new RegExp(pattern, 'g');
  let match = matcher.exec(text);

  while (match) {
    const first = match.index;
    const last = first + match[0].length - 1;

    if (!isWordChar(text[first - 1]) && !isWordChar(text[last + 1])) {
      return { start: chars[first].start, end: chars[last].end };
    }

    matcher.lastIndex = first + 1;
    match = matcher.exec(text);
  }

  return null;
};

const linkFirstMention = (html: string, name: string, href: string): string => {
  const pattern = normalize(name.trim())
    .split(/\s+/)
    .map(escapeRegExp)
    .join('\\s+');
  const openTags: string[] = [];
  const tagMatcher = new RegExp(TAG_PATTERN.source, 'g');
  let cursor = 0;

  // One extra pass after the last tag covers the trailing text.
  for (;;) {
    const tag = tagMatcher.exec(html);
    const textEnd = tag ? tag.index : html.length;
    const found =
      textEnd > cursor && !openTags.some((t) => HEADING_TAGS.includes(t))
        ? findMention(html.slice(cursor, textEnd), pattern)
        : null;

    if (found) {
      // The first mention is already a link, e.g. on a second run.
      if (openTags.includes('a')) return html;

      const start = cursor + found.start;
      const end = cursor + found.end;

      return `${html.slice(0, start)}<a href="${escapeAttribute(
        href
      )}">${html.slice(start, end)}</a>${html.slice(end)}`;
    }

    if (!tag) return html;

    const [, closing, tagName = ''] =
      tag[0].match(/^<(\/?)\s*([a-z][a-z0-9]*)/i) || [];
    const lowerName = tagName.toLowerCase();

    if (closing) {
      const openIndex = openTags.lastIndexOf(lowerName);
      if (openIndex !== -1) openTags.splice(openIndex);
    } else if (
      lowerName &&
      !VOID_TAGS.includes(lowerName) &&
      !/\/\s*>$/.test(tag[0])
    ) {
      openTags.push(lowerName);
    }

    cursor = tag.index + tag[0].length;
  }
};

/**
 * Links the first body-text mention of each name. Headings and existing links
 * are skipped, so running it twice changes nothing.
 */
export const linkFirstMentions = (
  html: string,
  mentions: MentionLink[]
): string =>
  mentions.reduce(
    (result, { name, href }) =>
      name?.trim() && href ? linkFirstMention(result, name, href) : result,
    html
  );
